import React, { useState } from "react";
import { ArrowRight, Play, Image, FileBox, Wand2, Share2 } from "lucide-react";
import { Section, Button, Badge } from "@vild/site-ui";
import { STARTERS } from "../constants";
import type { StarterPipeline } from "../types";

const PipelineVisual = ({ nodes }: { nodes: number }) => {
  return (
    <div className="flex items-center gap-1 my-6 opacity-80">
      <div className="w-8 h-8 rounded bg-zinc-800 border border-zinc-700 flex items-center justify-center">
        <FileBox className="w-4 h-4 text-zinc-500" />
      </div>

      <div className="h-px w-4 bg-zinc-700"></div>

      <div className="flex items-center -space-x-2">
        {Array.from({ length: Math.min(nodes, 3) }).map((_, i) => (
          <div
            key={i}
            className="w-8 h-8 rounded-full bg-zinc-900 border border-zinc-700 flex items-center justify-center z-10 relative"
          >
            <div className="w-2 h-2 rounded-full bg-zinc-500"></div>
          </div>
        ))}
        {nodes > 3 && <div className="text-[10px] text-zinc-500 pl-3">+{nodes - 3}</div>}
      </div>

      <div className="h-px w-4 bg-zinc-700"></div>

      <div className="w-8 h-8 rounded bg-zinc-800 border border-zinc-700 flex items-center justify-center">
        <Share2 className="w-4 h-4 text-zinc-500" />
      </div>
    </div>
  );
};

const RecipeCard: React.FC<{ starter: StarterPipeline }> = ({ starter }) => {
  const isVideo = starter.type === "VIDEO";

  return (
    <div className="group flex flex-col bg-zinc-900 border border-zinc-800 hover:border-zinc-600 rounded-xl overflow-hidden transition-all duration-300 hover:shadow-2xl hover:-translate-y-1">
      <div
        className={`h-2 w-full ${
          isVideo ? "bg-gradient-to-r from-blue-600 to-indigo-600" : "bg-gradient-to-r from-purple-600 to-pink-600"
        }`}
      ></div>

      <div className="p-6 flex flex-col flex-1">
        <div className="flex justify-between items-start mb-4">
          <div className={`p-2 rounded-lg ${isVideo ? "bg-blue-500/10 text-blue-400" : "bg-purple-500/10 text-purple-400"}`}>
            {isVideo ? <Play className="w-5 h-5" /> : <Image className="w-5 h-5" />}
          </div>
          <Badge className="bg-zinc-800 text-zinc-400 border-zinc-700">{starter.nodes} Nodes</Badge>
        </div>

        <h3 className="text-xl font-bold text-white mb-2 group-hover:text-blue-400 transition-colors">{starter.name}</h3>
        <p className="text-sm text-zinc-400 leading-relaxed mb-4">{starter.description}</p>

        <PipelineVisual nodes={starter.nodes} />

        <div className="mt-auto pt-6 border-t border-zinc-800/50">
          <Button variant="secondary" className="w-full justify-between group/btn" asLink={`/starters/${starter.id}`}>
            <span className="text-zinc-200">Deploy Recipe</span>
            <ArrowRight className="w-4 h-4 opacity-50 group-hover/btn:opacity-100 group-hover/btn:translate-x-1 transition-all" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export const Starters: React.FC = () => {
  const [filter, setFilter] = useState<"ALL" | "VIDEO" | "IMAGE">("ALL");
  const startersList = Object.values(STARTERS);

  const filteredStarters = startersList.filter((s) => filter === "ALL" || s.type === filter);

  return (
    <>
      <Section className="text-center pt-32 pb-12">
        <h1 className="text-4xl md:text-5xl font-bold mb-6">Pipeline Gallery</h1>
        <p className="text-xl text-zinc-400 max-w-2xl mx-auto mb-10">
          Don&apos;t start from scratch. Clone a production-ready pipeline and customize it in the runtime.
        </p>

        <div className="inline-flex p-1 bg-zinc-900 border border-zinc-800 rounded-lg">
          {["ALL", "VIDEO", "IMAGE"].map((type) => (
            <button
              key={type}
              onClick={() => setFilter(type as any)}
              className={`px-6 py-2 rounded-md text-sm font-medium transition-all ${
                filter === type ? "bg-zinc-800 text-white shadow-sm" : "text-zinc-500 hover:text-zinc-300"
              }`}
            >
              {type === "ALL" ? "All Pipelines" : type.charAt(0) + type.slice(1).toLowerCase()}
            </button>
          ))}
        </div>
      </Section>

      <Section className="pt-0 min-h-[600px]">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredStarters.map((starter) => (
            <RecipeCard key={starter.id} starter={starter} />
          ))}

          <div className="border border-dashed border-zinc-800 rounded-xl flex flex-col items-center justify-center p-8 text-center text-zinc-500 hover:border-zinc-700 hover:bg-zinc-900/30 transition-all cursor-pointer group">
            <div className="w-12 h-12 rounded-full bg-zinc-900 border border-zinc-800 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
              <Wand2 className="w-5 h-5" />
            </div>
            <h3 className="text-lg font-medium text-zinc-300 mb-1">Build Custom</h3>
            <p className="text-sm max-w-xs">Need something else? Use the node editor to build from scratch.</p>
          </div>
        </div>
      </Section>
    </>
  );
};

